import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  AppBar,
} from '@mui/material';

import NavBar from '../Navigation/Navigation';
import { debounce } from 'lodash';

interface PriceDropItem {
  Location_ID: string;
  createdLocation: string;
  Price_Change_No: number;
  PLU_SKU: string;
  Style_Code: string;
  Description_1: string;
  Color_Code: string;
  Size1_Code: string;
  Prepared_Date: string;
  Approved_Date: string;
  Applied_Date: string;
  Status: string;
  Value: number;
  Original_Retail: number;
  Org_Selling_Price: number;
  QTY_On_Hand: number | null;
  Average_Cost: number | null;
  Aged_Days: number;
  Notes: string | null;
}

const PriceDropByStyleView: React.FC = () => {
  const [styleCode, setStyleCode] = useState('');
  const [items, setItems] = useState<PriceDropItem[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async (style: string) => {
    if (!style.trim()) return;
    setLoading(true);
    try {
      const response = await fetch('/pc/priceDropStyle', {
        method: 'POST',
        credentials:'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ styleCode: style }),
      });
      const result = await response.json();
      setItems(result.records || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounced version of fetchData
  const debouncedFetch = useMemo(
    () => debounce((value: string) => fetchData(value), 500),
    []
  );

  useEffect(() => {
    debouncedFetch(styleCode);
    return () => {
      debouncedFetch.cancel();
    };
  }, [styleCode, debouncedFetch]);

  return (
    <>
      <AppBar position="sticky">
        <NavBar />
      </AppBar>

      {/* Table display */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', padding: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    'Price_Change_No','Style', 'Description',  'Color', 
                    'Qty', 'Avg. Cost', 'New Price', 'Original Retail',
                    'Status',  'Approved', 'Applied','Aged_Days'
                  ].map((header) => (
                    <TableCell key={header} sx={{
                      fontWeight: 'bold',
                      fontFamily: 'Arial',
                      fontSize: '11pt',
                      backgroundColor: '#f5ecc8',
                      color: 'black',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1
                    }}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={`${item.Style_Code}-${index}`} sx={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f5f5f5'}}>
                   <TableCell>{item.Price_Change_No}</TableCell>
                   
                    <TableCell>{item.Description_1}</TableCell>
                    <TableCell>{item.Style_Code}</TableCell>
                    <TableCell>{item.Color_Code}</TableCell>
                   
                    <TableCell>{item.QTY_On_Hand ?? '-'}</TableCell>
                    <TableCell>{item.Average_Cost?.toFixed(2) ?? '-'}</TableCell>
                    <TableCell>${item.Value.toFixed(2)}</TableCell>
                    <TableCell>${item.Original_Retail.toFixed(2)}</TableCell>
                    <TableCell>{item.Status}</TableCell>
                   
                    <TableCell>{new Date(item.Approved_Date).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(item.Applied_Date).toLocaleDateString()}</TableCell>
                    <TableCell>{item.Aged_Days}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Bottom Search Bar */}
      <Box sx={{
        position: 'sticky',
        bottom: 0,
        backgroundColor: '#fff',
        borderTop: '1px solid #ccc',
        padding: 1.5,
        zIndex: 10,
      }}>
        <TextField
          label="Enter Style Code"
          variant="outlined"
          value={styleCode}
          onChange={(e) => setStyleCode(e.target.value)}
          fullWidth
          InputProps={{ sx: { fontSize: '12pt' } }}
          InputLabelProps={{ sx: { fontWeight: 'bold' } }}
        />
      </Box>
    </>
  );
};

export default PriceDropByStyleView;
